import { TAGThemeProvider } from '@aspendental/shared-components-web';
import { AppContextProvider } from '@aspendental/shared-components-web/lib/context/AppContextProvider.ctx';
import { FacilityContextProvider } from '@aspendental/shared-components-web/lib/context/FacilityContextProvider.ctx';
import { TAGBrand } from '@aspendental/shared-components-web/lib/types';
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { StyledEngineProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Client, Provider as GraphQueryProvider, cacheExchange, fetchExchange } from 'urql';

import { useAppStore } from '@src/store';

const queryClient = new QueryClient();

const getGraphClient = (graphURL: string) => {
	return new Client({
		url: graphURL,
		exchanges: [cacheExchange, fetchExchange],
		requestPolicy: 'network-only',
		fetchOptions: () => {
			return {
				credentials: 'include',
				headers: {
					'x-client-application': process.env.APP_NAME,
				},
			};
		},
	});
};

export interface IContextProvidersProps {
	config: any;
	preview: boolean;
	environment: 'local' | 'dev' | 'stage' | 'prod';
	graphURL: string;
	user: any;
	children: React.ReactNode;
	googleURL: string;
	resultURL: string;
	googleMapApiKey: string;
	eapiBaseUrl: string;
	userEventsURL: string;
	appData: any;
}

export const ContextProviders = ({
	config,
	preview,
	environment,
	graphURL,
	user,
	children,
	googleURL,
	resultURL,
	googleMapApiKey,
	eapiBaseUrl,
	userEventsURL,
	appData,
}: IContextProvidersProps) => {
	const router = useRouter();
	const [isClient, setIsClient] = useState(false);

	const brandTheme = config?.themeId || process.env.TAG_BRAND_THEME || 'aspendental';
	const brandName = brandTheme as TAGBrand;
	const graphClient = getGraphClient(graphURL);

	useEffect(() => {
		setIsClient(true);
	}, []);

	const setRestUrl = useAppStore((store) => store.setRestUrl);
	const setGraphUrl = useAppStore((store) => store.setGraphUrl);
	const setGoogleUrl = useAppStore((store) => store.setGoogleUrl);
	const setUserEventsUrl = useAppStore((store) => store.setUserEventsUrl);
	const setGoogleMapApiKey = useAppStore((store) => store.setGoogleMapApiKey);
	const setEapiBaseUrl = useAppStore((store) => store.setEapiBaseUrl);
	const setIsPatientPortalPhase2Enabled = useAppStore((store) => store.setIsPatientPortalPhase2Enabled);

	useEffect(() => {
		setGoogleUrl(googleURL);
		setRestUrl(resultURL);
		setGraphUrl(graphURL);
		setUserEventsUrl(userEventsURL);
		setGoogleMapApiKey(googleMapApiKey);
		setEapiBaseUrl(eapiBaseUrl);
	}, [
		eapiBaseUrl,
		googleMapApiKey,
		googleURL,
		graphURL,
		resultURL,
		setEapiBaseUrl,
		setGoogleMapApiKey,
		setGoogleUrl,
		setGraphUrl,
		setRestUrl,
		setUserEventsUrl,
		userEventsURL,
	]);

	useEffect(() => {
		setIsPatientPortalPhase2Enabled(appData?.featureFlags?.enablePatientPortalPhase2);
	}, [appData?.featureFlags?.enablePatientPortalPhase2, setIsPatientPortalPhase2Enabled]);

	return (
		<GraphQueryProvider value={graphClient}>
			<QueryClientProvider client={queryClient}>
				<ContentfulLivePreviewProvider
					locale={router.locale || 'en-US'}
					enableInspectorMode={preview}
					enableLiveUpdates={preview}
				>
					<AppContextProvider
						config={config}
						theme={brandName}
						environment={environment}
						appName={process.env.APP_NAME || 'tag_dev'}
						appVersion={`${process.env.APP_VERSION || 'unknown'}(design-system:${
							process.env.DESIGN_SYSTEM_VERSION
						})`}
					>
						<StyledEngineProvider injectFirst>
							<TAGThemeProvider branding={brandName as TAGBrand}>
								{isClient && (
									<FacilityContextProvider
										appName="tag_dev"
										appVersion="0.0.0"
										brand={brandName as TAGBrand}
										user={user}
									>
										{children}
									</FacilityContextProvider>
								)}
							</TAGThemeProvider>
						</StyledEngineProvider>
					</AppContextProvider>
				</ContentfulLivePreviewProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</GraphQueryProvider>
	);
};
